

import { useState, useRef } from 'react';
import { useAuth } from '../hooks/use-auth';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Loader2, Image as ImageIcon } from 'lucide-react';
import type { Report } from '../types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { createReport, uploadReportImage, convertImageToJPEG } from '../lib/data';
import { imageErrorMessage } from '../lib/image-prepare';

interface ReportDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const reportTypes: { value: Report['type']; label: string }[] = [
  { value: 'technical_issue', label: 'Tehnički problem' },
  { value: 'spam', label: 'Spam ili prevara' },
  { value: 'harassment', label: 'Uznemiravanje ili maltretiranje' },
  { value: 'hate_speech', label: 'Govor mržnje' },
  { value: 'impersonation', label: 'Lažno predstavljanje' },
  { value: 'other', label: 'Ostalo' },
];

export default function ReportDialog({ isOpen, onOpenChange }: ReportDialogProps) {
  const { user } = useAuth();
  const [reportType, setReportType] = useState<Report['type'] | ''>('');
  const [description, setDescription] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetForm = () => {
    setReportType('');
    setDescription('');
    setImageFile(null);
    setImagePreview(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      resetForm();
    }
    onOpenChange(open);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setIsSubmitting(true);
      try {
        const file = e.target.files[0];
        const converted = await convertImageToJPEG(file);
        setImageFile(converted);
        const localUrl = URL.createObjectURL(converted);
        setImagePreview(localUrl);
      } catch (err) {
        console.error("Image conversion failed", err);
        toast({
          file: "components/report-dialog.tsx", code: "NN-ERR-039",
          variant: 'destructive', title: "Greška", description: imageErrorMessage(err)
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reportType || !description.trim() || !user) {
      toast({
        file: "components/report-dialog.tsx", code: "NN-VAL-037",
        variant: 'destructive', title: 'Nedostaju informacije', description: 'Molimo odaberite vrstu prijave i unesite opis.'
      });
      return;
    }
    setIsSubmitting(true);

    try {
      let imageUrl: string | undefined = undefined;
      if (imageFile) {
        imageUrl = await uploadReportImage(imageFile);
      }

      await createReport(user.id, reportType, description, imageUrl);

      toast({
        file: "components/report-dialog.tsx", code: "NN-SUC-038",
        title: "Prijava poslana", description: "Hvala vam na povratnim informacijama. Pregledat ćemo ih uskoro."
      });
      handleOpenChange(false);
    } catch (error: any) {
      console.error("Failed to submit report", error);
      toast({
        file: "components/report-dialog.tsx", code: "NN-ERR-039",
        variant: 'destructive', title: "Greška", description: error.message || "Nije moguće poslati vašu prijavu."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Prijavi problem</DialogTitle>
            <DialogDescription>
              Pomozite nam da shvatimo što se događa. Vaše povratne informacije su važne.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="type" className="text-right">
                Vrsta prijave
              </Label>
              <Select value={reportType} onValueChange={(value: Report['type']) => setReportType(value)}>
                <SelectTrigger id="type" className="col-span-3">
                  <SelectValue placeholder="Odaberite kategoriju..." />
                </SelectTrigger>
                <SelectContent>
                  {reportTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label htmlFor="description" className="text-right pt-2">
                Opis
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="col-span-3"
                placeholder="Molimo navedite što više detalja..."
                rows={5}
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Snimak zaslona</Label>
              <div className="col-span-3 space-y-2">
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isSubmitting} className="hover:bg-[#e73e7b]">
                  <ImageIcon className="mr-2 h-4 w-4" /> Priloži sliku
                </Button>
                <Input
                  id="report-image-upload"
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleImageChange}
                  accept="image/*"
                  disabled={isSubmitting}
                />
                {imageFile && <p className="text-sm text-muted-foreground truncate">{imageFile.name}</p>}
                {imagePreview && (
                  <div className="relative w-full aspect-video rounded-lg overflow-hidden border">
                    <img src={imagePreview} alt="Pregled slike" className="object-cover" />
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={!reportType || !description.trim() || isSubmitting} className="bg-accent hover:bg-[#e73e7b]">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Pošalji prijavu
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
